import { useState, useEffect, useMemo, useRef } from "react";
import { Icon } from "../components/icon";
import RecipientsTable from "../components/recipients-table";
import VoiceMessageSummary from "../components/VoiceMessageSummary";
import TextMessageSummary from "../components/TextMessageSummary";
import EmailSummary from "../components/EmailSummary";
import MessageContent from "../components/analytics/message-content";
import actions from "../actions/actions";
import { AlertType, MAX_DISPLAY } from "../utilities/constants";
import "../styles/analytic.scss";

// ─── Helpers ─────────────────────────────────────────────────────────────────

/** Formats a scheduled date value for display; falls back to the raw string if
 *  the value cannot be parsed as a valid date. */
function formatScheduledDate(scheduledDate) {
  if (!scheduledDate) return "";
  const d = new Date(scheduledDate);
  if (!isNaN(d.getTime())) {
    return d.toLocaleString(undefined, {
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  }
  return scheduledDate;
}

// ─── Sub-components ──────────────────────────────────────────────────────────

/** A single labelled key–value row in the message-details grid. */
function DetailItem({ label, value }) {
  return (
    <div className="message-details-item" aria-label={label}>
      <div className="md-label">{label}</div>
      <div className="md-value">{value}</div>
    </div>
  );
}

/**
 * Renders a pill list that collapses to MAX_DISPLAY items with expand / collapse
 * affordances.
 *
 * Props:
 *   items        – full item array
 *   renderPill   – (item, index) => ReactNode
 *   getItemTitle – (item) => string  — used for the "+N more" tooltip
 *   showAll      – controlled boolean
 *   onToggle     – (nextShowAll: boolean) => void
 */
function ExpandablePillList({
  items,
  renderPill,
  getItemTitle,
  showAll,
  onToggle,
}) {
  const visible = showAll ? items : items.slice(0, MAX_DISPLAY);
  const extras = showAll ? [] : items.slice(MAX_DISPLAY);
  const extraTitle = extras.map(getItemTitle).filter(Boolean).join(", ");

  const handleKey = (e, action) => {
    if (e.key === "Enter" || e.key === " ") action();
  };

  return (
    <div className="md-pill-list">
      {visible.map(renderPill)}

      {extras.length > 0 && (
        <span
          className="md-pill"
          title={extraTitle}
          role="button"
          tabIndex={0}
          onClick={() => onToggle(true)}
          onKeyDown={(e) => handleKey(e, () => onToggle(true))}
        >
          +{extras.length} more
        </span>
      )}

      {showAll && items.length > MAX_DISPLAY && (
        <span
          className="md-pill"
          role="button"
          tabIndex={0}
          onClick={() => onToggle(false)}
          onKeyDown={(e) => handleKey(e, () => onToggle(false))}
        >
          Show less
        </span>
      )}
    </div>
  );
}

// ─── Page component ──────────────────────────────────────────────────────────

export default function Analytic({ alert }) {
  // ── Derived alert data ──────────────────────────────────────────────────

  const scheduledDisplay = useMemo(
    () => formatScheduledDate(alert?.scheduledDate),
    [alert?.scheduledDate],
  );

  const groups = useMemo(() => alert?.groups ?? [], [alert]);

  const { regularGroups, tempGroups } = useMemo(() => {
    const regular = [];
    const temp = [];
    for (const g of groups) {
      if (g?.is_temp === 1) temp.push(g);
      else regular.push(g);
    }
    return { regularGroups: regular, tempGroups: temp };
  }, [groups]);

  // ── UI state ────────────────────────────────────────────────────────────

  const [groupMembers, setGroupMembers] = useState([]);
  const [showAllGroups, setShowAllGroups] = useState(false);
  const [showAllMembers, setShowAllMembers] = useState(false);

  // Summary data populated by RecipientsTable callbacks
  const [voiceSummary, setVoiceSummary] = useState(null);
  const [smsSummary, setSmsSummary] = useState(null);
  const [emailSummary, setEmailSummary] = useState(null);

  // Active tab / filter index for each channel
  const [voiceTabStatus, setVoiceTabStatus] = useState(0);
  const [smsTabStatus, setSmsTabStatus] = useState(0);
  const [emailTabStatus, setEmailTabStatus] = useState(0);

  // ── Side-effects ─────────────────────────────────────────────────────────

  const tempGroupId = tempGroups[0]?.id;
  useEffect(() => {
    if (!tempGroupId) {
      setGroupMembers([]);
      return;
    }

    let cancelled = false;
    actions.groups
      .getAllGroupMembers(tempGroupId)
      .then((details) => {
        if (!cancelled) setGroupMembers(details?.members ?? []);
      })
      .catch((err) => console.error("Error fetching group details:", err));

    return () => {
      cancelled = true;
    };
  }, [tempGroupId]);

  // ── Render ───────────────────────────────────────────────────────────────

  return (
    <div className="analytics-page" role="tabpanel" aria-label="Analytics">
      {/* ── Message Details ─────────────────────────────────────────────── */}
      <section className="message-details" aria-label="Message Details">
        <h2 className="message-details-title">Message Details</h2>

        <div className="message-details-grid">
          <DetailItem label="Sent By" value={alert?.senderName} />
          <DetailItem label="Sent On" value={scheduledDisplay || "-"} />
          <DetailItem label="Notification" value={alert.notification} />
          <DetailItem label="Segmentation" value={alert.segmentation} />
          <DetailItem
            label="Priority"
            value={alert?.isEmergency ? "Emergency" : "Non-Emergency"}
          />
        </div>

        {regularGroups.length > 0 && (
          <div className="message-details-groups">
            <div className="md-label">Groups</div>
            <ExpandablePillList
              items={regularGroups}
              showAll={showAllGroups}
              onToggle={setShowAllGroups}
              getItemTitle={(g) => g?.name ?? ""}
              renderPill={(group, index) => (
                <span
                  key={group?.id ?? group?.groupId ?? index}
                  className="md-pill"
                >
                  <Icon icon="users" className="fas" />{" "}
                  <span>{group?.name}</span>
                </span>
              )}
            />
          </div>
        )}

        {groupMembers.length > 0 && (
          <div className="message-details-groups">
            <div className="md-label">Individual</div>
            <ExpandablePillList
              items={groupMembers}
              showAll={showAllMembers}
              onToggle={setShowAllMembers}
              getItemTitle={(m) =>
                [m?.firstName, m?.lastName].filter(Boolean).join(" ")
              }
              renderPill={(member, index) => (
                <span key={member.userId ?? index} className="md-pill">
                  <Icon icon="user" className="fas" />{" "}
                  <span>
                    {member.firstName} {member.lastName}
                  </span>
                </span>
              )}
            />
          </div>
        )}
      </section>

      {/* ── Channel Summary ──────────────────────────────────────────────── */}
      <section
        className="voicemsg-smry-section"
        aria-label="Voice Message Summary"
      >
        {alert?.alertType === AlertType.Voice && (
          <VoiceMessageSummary
            summary={voiceSummary}
            onFilterChange={setVoiceTabStatus}
          />
        )}
        {alert?.alertType === AlertType.Text && (
          <TextMessageSummary
            summary={smsSummary}
            onFilterChange={setSmsTabStatus}
          />
        )}
        {alert?.alertType === AlertType.Email && (
          <EmailSummary
            summary={emailSummary}
            onFilterChange={setEmailTabStatus}
          />
        )}
      </section>

      {/* ── Recipients ──────────────────────────────────────────────────── */}
      <section className="recipients-section" aria-label="Recipients">
        <h2 className="recipients-title">Recipients</h2>
        <RecipientsTable
          alert={alert}
          onSummaryLoaded={setVoiceSummary}
          voiceTabStatus={voiceTabStatus}
          onVoiceTabChange={setVoiceTabStatus}
          smsTabStatus={smsTabStatus}
          onSmsTabChange={setSmsTabStatus}
          onSmsSummaryLoaded={setSmsSummary}
          emailTabStatus={emailTabStatus}
          onEmailTabChange={setEmailTabStatus}
          onEmailSummaryLoaded={setEmailSummary}
        />
      </section>

      {/* ── Message Content ─────────────────────────────────────────────── */}
      <MessageContent alert={alert} />
    </div>
  );
}
